import { type ChangeEvent, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { logToSentry, useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import {
    AlgoliaAnalyticsEventAction,
    AlgoliaAnalyticsEventPage,
    AlgoliaAnalyticsEventSource,
    GA4EventName,
    sendAlgoliaFiltersClickedEvent,
    sendAlgoliaFiltersViewedEvent,
    trackFilterInteraction,
} from '@jsmdg/tracking';
import {
    FilterDropdownBase,
    RadioButton,
    RadioButtonGroup,
    type SelectOption,
    TripBoldIcon,
} from '@jsmdg/yoshi';
import { type FragmentContext } from '../../../../../../shared/types/fragmentContext';
import { type Filter } from '../../../../../../shared/types/search';
import { getActiveFilters } from '../../../../../hooks/getActiveFilters';
import { useInsightsClient } from '../../../../../hooks/useInsightsClient';
import styles from '../common.module.scss';

type CategoriesSelectProps = {
    readonly categories: SelectOption[];
    readonly onSelect: (category: SelectOption) => void;
    readonly filter: Filter;
    readonly scrollToElement: () => void;
};

const categoryFacet = 'categories';
const maxFiltersPerEvent = 10;

const normalizeCategoryLabel = (label: string): string =>
    label.replaceAll(/[\s-]+/g, '').toLowerCase();

const messages = defineMessages({
    label: {
        defaultMessage: 'Was?',
    },
    selectExperience: {
        defaultMessage: 'Erlebnis auswählen',
    },
    a11ySubmitText: {
        defaultMessage: 'Filter anwenden',
    },
    a11yIndicatorText: {
        defaultMessage: 'Filter zurücksetzen',
    },
    a11yLegendText: {
        defaultMessage: `Kategorie {categoryName} auswählen`,
    },
});

const handleClickOutside = (): void => {};

const CategoriesSelect = ({
    categories,
    filter,
    onSelect,
    scrollToElement,
}: CategoriesSelectProps): JSX.Element => {
    const intl = useIntl();
    const { algoliaTracking, tenantConfig } = useFragmentContext<FragmentContext>();
    const insightsClientRef = useInsightsClient();
    const filterRef = useRef<HTMLDivElement>(null);
    const [selectedCategory, setSelectedCategory] = useState<SelectOption>();

    const isAllExperiencesLabel = (label: string): boolean =>
        normalizeCategoryLabel(label) === normalizeCategoryLabel(tenantConfig.allExperiencesLabel);

    const categoryLabels = categories
        .map(category => category.label)
        .filter((label): label is string => Boolean(label) && !isAllExperiencesLabel(label));

    const sendCategoryFilterEvent = async (
        action: AlgoliaAnalyticsEventAction,
        labels: string[],
    ): Promise<void> => {
        if (!labels.length || !algoliaTracking?.userToken || !algoliaTracking.indexName) {
            return;
        }

        await window.yieldToMainThread?.();

        try {
            const insightsClient = (await insightsClientRef.current)?.default;

            if (!insightsClient) {
                return;
            }

            const eventName = {
                action,
                page: AlgoliaAnalyticsEventPage.Home,
                source: AlgoliaAnalyticsEventSource.ExperienceFinder,
            };
            const filters = labels.map(label => `${categoryFacet}:${label}`);
            const sendFilters =
                action === AlgoliaAnalyticsEventAction.FilterViewed
                    ? sendAlgoliaFiltersViewedEvent
                    : sendAlgoliaFiltersClickedEvent;

            for (let start = 0; start < filters.length; start += maxFiltersPerEvent) {
                sendFilters({
                    eventName,
                    filters: filters.slice(start, start + maxFiltersPerEvent),
                    indexName: algoliaTracking.indexName,
                    insightsClient,
                });
            }
        } catch (error) {
            logToSentry(error instanceof Error ? error : new Error('Algolia Insights failed'));
        }
    };

    const handleOnOpen = (): void => {
        scrollToElement();
        void sendCategoryFilterEvent(AlgoliaAnalyticsEventAction.FilterViewed, categoryLabels);
    };

    const handleOnChange = async (selection: string, _evt: ChangeEvent): Promise<void> => {
        const label = categories.find(option => option.value === selection)?.label;

        const category = { label: label ?? tenantConfig.allExperiencesLabel, value: selection };
        setSelectedCategory(category);
        onSelect(category);
        // close filter dropdown
        filterRef.current?.querySelector('button')?.click();

        if (label) {
            await window.yieldToMainThread();
            if (isAllExperiencesLabel(label)) {
                trackFilterInteraction(
                    'Reset_Category',
                    undefined,
                    {
                        eventName: GA4EventName.ResetFilter,
                        filter_type: 'Category',
                    },
                    getActiveFilters(filter),
                );
            } else {
                trackFilterInteraction(
                    'SetCategory',
                    label,
                    {
                        eventName: GA4EventName.SetFilter,
                        filter_type: 'Category',
                        filter_value: label,
                    },
                    getActiveFilters(filter, ['Category']),
                );
                void sendCategoryFilterEvent(AlgoliaAnalyticsEventAction.FilterClicked, [label]);
            }
        }
    };

    return (
        <div
            className={classNames(styles.wrapper, 'd-flex flex-sm-column w-100 p-0', {
                [styles.isActive]: !!selectedCategory,
            })}
            ref={filterRef}
        >
            <div className="d-flex align-items-start">
                <TripBoldIcon ariaHidden className={classNames(styles.icon, 'ml-1x mr-0-5x')} />
                <p className={classNames(styles.label, 'd-none d-sm-block fw-semibold mb-0')}>
                    {intl.formatMessage(messages.label)}
                </p>
            </div>

            <FilterDropdownBase
                className={styles.filterDropdown}
                label={selectedCategory?.label ?? tenantConfig.allExperiencesLabel}
                hasFormFooter={false}
                onClickOutside={handleClickOutside}
                hasChanged
                filterDropdownButtonClasses={classNames(
                    styles.filterButton,
                    'w-100 py-0 pl-0-5x pl-sm-2x justify-content-between',
                    { [styles.placeholder]: !selectedCategory },
                )}
                filterDropdownOverlayClasses={classNames(
                    styles.filterDropdownOverlay,
                    'mt-2x mt-sm-1-5x pb-0',
                )}
                onOpen={handleOnOpen}
                a11yButtonText={intl.formatMessage(messages.selectExperience)}
                a11ySubmitText={intl.formatMessage(messages.a11ySubmitText)}
                a11yIndicatorText={intl.formatMessage(messages.a11yIndicatorText)}
            >
                <RadioButtonGroup
                    name="categories"
                    vertical
                    className={classNames(styles.list, 'px-3x')}
                    value={selectedCategory?.value ?? categories[0]?.value}
                    onChange={handleOnChange}
                    a11yLegendText={intl.formatMessage(messages.a11yLegendText, {
                        categoryName: selectedCategory?.label ?? tenantConfig.allExperiencesLabel,
                    })}
                >
                    {categories.map(category => {
                        return (
                            <RadioButton
                                value={category.value}
                                key={category.label}
                                id={category.label}
                            >
                                <span>{category.label}</span>
                            </RadioButton>
                        );
                    })}
                </RadioButtonGroup>
            </FilterDropdownBase>
        </div>
    );
};

export { CategoriesSelect };
